import type { QueryTarget } from "@/shared/domain/query";
import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import { CreateViewRequest, UpdateViewRequest, ViewRecord } from "./ViewRecord";

/**
 * The six View endpoints, without React. Every route is gated on the
 * `release-views` flag and returns 403 until it is enabled for the caller's org.
 */
export class ViewService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createView(
    request: CreateViewRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<ViewRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/views/create`);
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<ViewRecord>();
  }

  public async getViewById(
    viewId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<ViewRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/views/id/${viewId}`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<ViewRecord>();
  }

  /**
   * The Views an org holds for one target. `target` is required: a list spanning
   * targets would offer Views that cannot be applied where they are shown. The
   * backend returns only what the caller may see.
   */
  public async getViewsForOrg(
    target: QueryTarget,
    orgId: string,
    options?: Partial<{
      pageToken: string;
      abortSignal: AbortSignal;
    }>,
  ): Promise<PaginatedResponse<ViewRecord>> {
    const queryParams = new URLSearchParams({ target });
    if (options?.pageToken !== undefined) {
      queryParams.set("page_token", options.pageToken);
    }
    // No trailing slash: `v1/views/?...` does not match the route and 404s.
    const requestUrl = this.#httpClient.constructUrl(`v1/views`, queryParams);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<PaginatedResponse<ViewRecord>>();
  }

  /**
   * Every View an org holds for one target, with `next_token` followed until the
   * list is exhausted.
   *
   * A page can come back short *and* carry a token: the endpoint caps how many
   * reads it spends filling one page, so an org whose Views mostly belong to
   * other people is paged well below the nominal page size. Reading only the
   * first response would drop the rest with nothing to show it happened.
   */
  public async getAllViews(
    target: QueryTarget,
    orgId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<ViewRecord[]> {
    const collected: ViewRecord[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const page = await this.getViewsForOrg(target, orgId, {
        pageToken,
        abortSignal: options?.abortSignal,
      });
      collected.push(...page.items);
      // `next_token` is `string | null` on the wire; coerce null to undefined
      // so the loop predicate stays a single check.
      pageToken = page.next_token || undefined;
    } while (pageToken !== undefined);

    return collected;
  }

  public async updateView(
    viewId: string,
    request: UpdateViewRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<ViewRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/views/id/${viewId}`);
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<ViewRecord>();
  }

  public async deleteView(
    viewId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(`v1/views/id/${viewId}`);
    await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
  }

  /**
   * Copy a View, with the caller as the new one's author.
   *
   * Server-side rather than read-then-create, so the definition is copied whole
   * — re-posting a parsed one would drop fields this build predates. Omit
   * `request` to take the source's, named "<name> (copy)".
   */
  public async duplicateView(
    viewId: string,
    orgId: string,
    options?: Partial<{
      request: CreateViewRequest;
      abortSignal: AbortSignal;
    }>,
  ): Promise<ViewRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/views/id/${viewId}/duplicate`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body:
        options?.request !== undefined
          ? JSON.stringify(options.request)
          : undefined,
    });
    return response.json<ViewRecord>();
  }
}
